<template>
  <div class="mission-asset-map">
    <mapbox
      :height="mapHeight"
      @map-loaded="onMapLoaded"
      @map-styledata="onMapStyleChange"
      ref="mapbox"
    />
  </div>
</template>

<script>
import moment from "moment";
import mapboxgl from "mapbox-gl";
import Mapbox from "@/components/Mapbox/Mapbox";
import { htmlspecialchars } from "@/helpers/security";
import { AssetService } from "@/services/asset.service";
import { MissionAssetService } from "@/services/missionAsset.service";

export default {
  colors: {
    pass: "#7CB342",
    pass_fault: "#FB8C00",
    fail: "#e53935",
    selected: "#0288D1",
  },
  layers: {
    default: "layer-assets",
    selected: "layer-assets-selected",
  },
  sources: {
    default: "source-assets",
    selected: "source-assets-selected",
  },
  props: {
    missionAssets: {
      type: Array,
      default: () => [],
    },
    mapHeight: {
      type: [Number, String],
      default: 500,
    },
    handleFeatureClick: {
      type: Function,
    },
  },
  components: {
    Mapbox,
  },
  data: () => ({
    mapInstance: null,
    geojsonAssets: {
      type: "FeatureCollection",
      features: [],
    },
    geojsonAssetsFetched: false,
    geojsonAssetsSelected: {
      type: "FeatureCollection",
      features: [],
    },
    geojsonAssetsSelectedInitial: false,
    missionAssetsPending: [],
    popup: null,
  }),
  watch: {
    missionAssets() {
      this.loadMissionAssets();
    },
  },
  methods: {
    async loadMissionAssets() {
      if (!this.mapInstance) return;

      // Clear geojson sources data
      this.clearGeojsons();

      // Move this to API if performance is an issue
      // Get latest mission assets. If duplicate, get latest mission asset.
      const missionAssetsLatest = this.missionAssets.reduce(
        (acc, missionAsset) => {
          let foundIndex = acc.findIndex(
            (x) => x.coreId === missionAsset.coreId
          );

          if (foundIndex > -1) {
            if (
              moment(missionAsset.createdAt).isAfter(acc[foundIndex].createdAt)
            ) {
              // Replace
              acc[foundIndex] = missionAsset;
            }
          } else {
            acc.push(missionAsset);
          }

          return acc;
        },
        []
      );

      // Get mission assets core (stormport) ids
      const assetIds = missionAssetsLatest.map(
        (missionAsset) => missionAsset.coreId
      );

      let geojsonAssets = this.geojsonAssets;

      // Make sure we have ids to pass to AssetService.geojson as it will return all assets if no ids are passed
      if (assetIds.length) {
        geojsonAssets = await AssetService.geojson({
          ids: assetIds,
        }).then((response) => response.data);

        this.geojsonAssetsFetched = true;

        // Check for missing mission assets
        this.checkMissingMissionAssets(
          assetIds,
          geojsonAssets,
          missionAssetsLatest
        );

        // Modify features. Add custom properties
        geojsonAssets.features = geojsonAssets.features.map((feature) => {
          let status = "";
          let description = "";

          const missionAsset = missionAssetsLatest.find(
            (missionAsset) =>
              missionAsset.coreId === feature.properties.asset_id
          );
          if (missionAsset) {
            // Get mission/inspection status
            status = missionAsset.passFail;

            if (status === "pass" && missionAsset.isFault) {
              status = "pass_fault";
            }

            let description_lines = [];

            description_lines.push(`<strong>${htmlspecialchars(missionAsset.thirdPartyCode || missionAsset.coreId)}</strong>`);
            description_lines.push('<p>');
            description_lines.push(`New Asset: ${missionAsset.newAsset ? 'YES' : 'NO'}`);
            description_lines.push('<br />');
            if (missionAsset.passFail !== 'pass') {
              const contract = this.$store.getters['contracts/selected'];
              const pass_fail_question_code = contract.config.pass_fail_test_question_code;
              const qaObject = missionAsset.qa.find(qa => {
                return qa.code === pass_fail_question_code;
              });
              let answers = qaObject ? qaObject.answers : [];
              answers = answers.map(a => {
                return a.name;
              });
              description_lines.push(`Successful Clean: NO`);
              description_lines.push('<br />');
              description_lines.push(`Reason For Cancellation: ${htmlspecialchars(answers.join(', '))}`);
            }
            else {
              description_lines.push(`Successful Clean: YES`);
            }
            description_lines.push('<br />');
            if (missionAsset.isFault) {
              let faults = missionAsset.faults.map(fault => {
                return fault.name;
              });
              description_lines.push(`Faults Reported: ${htmlspecialchars(faults.join(', '))}`);
            }
            else {
              description_lines.push(`Faults Reported: NONE`);
            }
            description_lines.push('</p>');

            description = description_lines.join('');
          }

          feature.properties.mission_status = status;
          feature.properties.description = description;
          // Set asset_id as the feature's id
          feature.id = feature.properties.asset_id;

          return feature;
        });
      }

      this.geojsonAssets = geojsonAssets;
      this.updateMapLayers();

      // Fit to bounds
      if (geojsonAssets.features.length) {
        const bounds = new mapboxgl.LngLatBounds();

        geojsonAssets.features.forEach(function (feature) {
          bounds.extend(feature.geometry.coordinates);
        });

        this.mapInstance.fitBounds(bounds, { padding: 30 });
      }

      if (this.missionAssetsPending.length) {
        // Get last pending mission asset
        const missionAssetPending = this.missionAssetsPending[
          this.missionAssetsPending.length - 1
        ];
        // Select mission asset
        this.selectFeatureByMissionAsset(missionAssetPending);
        // Clear pending mission assets
        this.missionAssetsPending = [];
      }
    },
    clearGeojsons() {
      this.geojsonAssets = {
        type: "FeatureCollection",
        features: [],
      };
      this.geojsonAssetsSelected = {
        type: "FeatureCollection",
        features: [],
      };
    },
    checkMissingMissionAssets(assetIds, geojson, missionAssets) {
      const geojsonAssetIds = geojson.features.map(
        (feature) => feature.properties.asset_id
      );
      const assetIdsMissing = assetIds.filter(
        (assetId) => !geojsonAssetIds.includes(assetId)
      );
      const missionAssetsMissing = missionAssets.filter((missionAsset) =>
        assetIdsMissing.includes(missionAsset.coreId)
      );
      this.$emit("fetch-missing", missionAssetsMissing);
    },
    updateMapLayers() {
      if (!this.mapInstance) return;

      const paintDefault = {
        // "circle-color": [
        //   "case",
        //   ["boolean", ["feature-state", "selected"], false],
        //   this.$options.colors.selected,
        //   [
        //     "match",
        //     ["get", "mission_status"],
        //     "pass",
        //     this.$options.colors.pass,
        //     "pass_fault",
        //     this.$options.colors.pass_fault,
        //     "fail",
        //     this.$options.colors.fail,
        //     "#757575"
        //   ]
        // ],
        "circle-color": [
          "match",
          ["get", "mission_status"],
          "pass",
          this.$options.colors.pass,
          "pass_fault",
          this.$options.colors.pass_fault,
          "fail",
          this.$options.colors.fail,
          "#757575",
        ],
        // "circle-radius": [
        //   "case",
        //   ["boolean", ["feature-state", "selected"], false],
        //   8,
        //   5
        // ],
        "circle-stroke-color": "#212121",
        "circle-stroke-width": 2,
      };

      // Default source/layer
      const sourceAssets = this.mapInstance.getSource(
        this.$options.sources.default
      );

      if (sourceAssets) {
        // Update source data
        sourceAssets.setData(this.geojsonAssets);
      } else {
        // Create source/layer
        this.mapInstance.addSource(this.$options.sources.default, {
          type: "geojson",
          data: this.geojsonAssets,
        });
        this.mapInstance.addLayer({
          id: this.$options.layers.default,
          type: "circle",
          source: this.$options.sources.default,
          paint: paintDefault,
        });
      }

      this.mapInstance.off(
        "click",
        this.$options.layers.default,
        this.onMapAssetsLayerClick
      );
      this.mapInstance.off(
        "mouseenter",
        this.$options.layers.default,
        this.onMapAssetsLayerMouseenter
      );
      this.mapInstance.off(
        "mouseleave",
        this.$options.layers.default,
        this.onMapAssetsLayerMouseleave
      );
      this.mapInstance.on(
        "click",
        this.$options.layers.default,
        this.onMapAssetsLayerClick
      );
      this.mapInstance.on(
        "mouseenter",
        this.$options.layers.default,
        this.onMapAssetsLayerMouseenter
      );
      this.mapInstance.on(
        "mouseleave",
        this.$options.layers.default,
        this.onMapAssetsLayerMouseleave
      );

      // Selected source/layer
      const sourceAssetsSelected = this.mapInstance.getSource(
        this.$options.sources.selected
      );

      if (sourceAssetsSelected) {
        // Update source data
        sourceAssetsSelected.setData(this.geojsonAssetsSelected);
      } else {
        // Create source/layer
        this.mapInstance.addSource(this.$options.sources.selected, {
          type: "geojson",
          data: this.geojsonAssetsSelected,
        });
        this.mapInstance.addLayer({
          id: this.$options.layers.selected,
          type: "circle",
          source: this.$options.sources.selected,
          paint: {
            ...paintDefault,
            "circle-radius": 8,
          },
        });
      }
    },
    selectFeature(feature = null, options = {}) {
      if (!this.mapInstance) return;

      const options_ = {
        deselect: true,
        multiple: false,
        ...options,
      };

      if (feature) {
        const featureSelectedIndex = this.geojsonAssetsSelected.features.findIndex(
          (geojsonAssetSelectedFeature) =>
            geojsonAssetSelectedFeature.id === feature.id
        );

        if (featureSelectedIndex > -1) {
          if (options_.deselect) {
            this.geojsonAssetsSelected.features.splice(featureSelectedIndex, 1);
          }
        } else {
          if (!options_.multiple) {
            this.geojsonAssetsSelected.features = [];
          }
          this.geojsonAssetsSelected.features.push(feature);
          
          let zoom = this.mapInstance.getZoom();

          // Choose a relatively close-up level where streets are clear
          // Apply only to first selection
          if (!this.geojsonAssetsSelectedInitial) {
            zoom = 18;
            this.geojsonAssetsSelectedInitial = true;
          }

          this.mapInstance.flyTo({
            center: feature.geometry.coordinates,
            zoom
          });
        }
      } else {
        this.geojsonAssetsSelected.features = [];
      }

      // Update selected source data
      this.mapInstance
        .getSource(this.$options.sources.selected)
        .setData(this.geojsonAssetsSelected);
    },
    selectFeatureByMissionAsset(missionAsset = null, options = {}) {
      // if geojson assets not yet fetched, store mission asset to be selected later
      if (!this.geojsonAssetsFetched) {
        if (missionAsset) {
          this.missionAssetsPending.push(missionAsset);
        }
      }

      let feature = null;

      if (missionAsset) {
        feature = this.geojsonAssets.features.find(
          (i) => i.id == missionAsset.coreId
        );
      }

      this.selectFeature(feature, options);
    },
    onMapLoaded() {
      this.mapInstance = this.$refs.mapbox.getMapInstance();
      this.loadMissionAssets();
    },
    onMapStyleChange() {
      // When a user switches between styles (streets, satellite, ...) all assets disappear from map
      // Re-add sources/layers on each style change
      this.updateMapLayers();
    },
    onMapAssetsLayerClick(e) {
      if (this.handleFeatureClick) {
        this.handleFeatureClick(e.features[0]);
      } else {
        this.selectFeature(e.features[0]);
      }
    },
    onMapAssetsLayerMouseenter(e) {
      // Change the cursor to a pointer when the mouse is over the places layer.
      this.mapInstance.getCanvas().style.cursor = "pointer";

      if (!this.popup) {
        this.popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });
      }
      let coordinates = e.features[0].geometry.coordinates.slice();
      let description = e.features[0].properties.description;
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      this.popup
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.mapInstance);
    },
    onMapAssetsLayerMouseleave() {
      // Change it back to a pointer when it leaves.
      this.mapInstance.getCanvas().style.cursor = "";
      this.popup.remove();
    },
  },
};
</script>
